// Comprehensive service following the data flow from README.md
// Flow: Manual Task -> Pipeline Instance -> Pipeline Config -> Entity Data

import { get, post, put } from '../utils/apiUtils';
import { getUserData } from '../utils/authUtils';

/**
 * Fetch JSON data from public/assets/ref folder
 * Using fetch API since the files are now in the public directory
 */
const fetchJSON = async (path) => {
  const response = await fetch(`/assets/ref/${path}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch Pipeline Configuration by pipeline code
 * GET /api/v1/mms_ai_configs/pipeline/config/get_by_cd/{pipeline_cd}
 */
export const fetchWorkflowConfigByCode = async (pipelineCode) => {
  try {
    const response = await get(`/api/v1/mms_ai_configs/pipeline/config/get_by_cd/${pipelineCode}`);
    return response;
  } catch (error) {
    console.error('Error fetching workflow config:', error);
    throw error;
  }
};

/**
 * Fetch entity configuration by entity code
 * GET /api/v1/mms_ai_configs/entities/config/get_by_cd/{entity_code}
 * @param {string} entityCode - Entity code (e.g., "AI_CARD_CLAIM_UNDERSTANDING")
 * @returns {Promise<Object>} Entity configuration
 */
export const fetchEntityConfig = async (entityCode) => {
  try {
    const response = await get(`/api/v1/mms_ai_configs/entities/config/get_by_cd/${entityCode}`);
    return response;
  } catch (error) {
    console.error(`Error fetching entity config ${entityCode}:`, error);
    throw error;
  }
};

/**
 * Fetch entity instance data by entity code and ID
 * GET /api/v1/mms_ai_entity_service/entity/data/{entity_code}/{entity_id}
 * @param {string} entityCode - Entity code (e.g., "AI_CARD_CLAIM_UNDERSTANDING")
 * @param {number} entityId - Entity ID
 * @returns {Promise<Object>} Entity instance data
 */
export const fetchEntityData = async (entityCode, entityId) => {
  try {
    const response = await get(`/api/v1/mms_ai_entity_service/entity/data/${entityCode}/${entityId}`);
    
    // Handle response structure: { status: "SUCCESS", result: [...] }
    if (response.status === 'SUCCESS' && Array.isArray(response.result) && response.result.length > 0) {
      return response.result[0]; // Return first item from result array
    }
    
    // Fallback if response structure is different
    return response;
  } catch (error) {
    console.error(`Error fetching entity ${entityCode}:${entityId}:`, error);
    throw error;
  }
};

/**
 * Fetch all entities from associated_entities array
 * @param {Array} associatedEntities - Array of {entity_cd, id, data} objects
 * @returns {Promise<Array>} Array of fetched entity data
 */
export const fetchAssociatedEntities = async (associatedEntities) => {
  if (!Array.isArray(associatedEntities) || associatedEntities.length === 0) {
    return [];
  }

  try {
    const results = [];
    
    for (let i = 0; i < associatedEntities.length; i++) {
      const entity = associatedEntities[i];
      
      // If data is already embedded, use it
      if (entity.data) {
        results.push({
          entity_cd: entity.entity_cd,
          id: entity.id,
          data: entity.data
        });
        continue;
      }
      
      const entityId = entity.id ?? entity.entity_id ?? entity.entity_ref;
      if (!entityId) {
        console.warn(`No entity id found for entity_cd ${entity.entity_cd}`);
        continue;
      }

      // Otherwise fetch from API
      try {
        const data = await fetchEntityData(entity.entity_cd, entityId);
        results.push({
          entity_cd: entity.entity_cd,
          id: entityId,
          data: data
        });
        
        // Add small delay between requests to prevent "too rapid" errors
        if (i < associatedEntities.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      } catch (error) {
        console.warn(`Failed to fetch entity ${entity.entity_cd}:${entityId}`, error);
      }
    }

    return results;
  } catch (error) {
    console.error('Error fetching associated entities:', error);
    return [];
  }
};

/**
 * Fetch Pipeline Execution by pipeline_instance_id
 * GET /api/v1/mms_ai_workflows/workflow/pipeline/execution/by_instance_id/{pipeline_instance_id}
 * @param {string} pipelineInstanceId - Pipeline instance ID (GUID)
 * @returns {Promise<Object>} Pipeline execution data
 */
export const fetchWorkflowInstanceFromAPI = async (pipelineInstanceId) => {
  try {
    const response = await get(`/api/v1/mms_ai_workflows/workflow/pipeline/execution/by_instance_id/${pipelineInstanceId}`);
    return response;
  } catch (error) {
    console.error('Error fetching workflow instance:', error);
    throw error;
  }
};

/**
 * Step 1: Fetch Manual Task Instance from API
 * This is the entry point for the Case Detail screen
 */
export const fetchManualTaskInstanceFromAPI = async (manualTaskId) => {
  try {
    const response = await get(`/api/v1/mms_ai_workflows/workflow/manual_task_instance/byId/${manualTaskId}`);
    return response;
  } catch (error) {
    console.error('Error fetching manual task instance from API:', error);
    throw error;
  }
};

/**
 * Step 1: Fetch Manual Task Instance (Mock - for backward compatibility)
 * This is the entry point for the Case Detail screen
 */
export const fetchManualTaskInstance = async (manualTaskId = 1) => {
  await new Promise(resolve => setTimeout(resolve, 200));
  
  // In a real app, this would be: GET /api/manual-tasks/{manualTaskId}
  const data = await fetchJSON('instances/manual_task_instance.json');
  return data;
};

/**
 * Step 2: Fetch Pipeline Instance using workflow_instance_id from manual task
 */
export const fetchPipelineInstance = async (workflowInstanceId) => {
  await new Promise(resolve => setTimeout(resolve, 200));
  
  // In a real app: GET /api/pipeline-instances/{workflowInstanceId}
  const data = await fetchJSON('instances/pipeline_instance.json');
  if (data.workflow_instance_id === workflowInstanceId) {
    return data;
  }
  throw new Error(`Pipeline instance not found for workflow_instance_id: ${workflowInstanceId}`);
};

/**
 * Step 3: Fetch Pipeline Config using workflow_code from pipeline instance
 */
export const fetchPipelineConfig = async (workflowCode) => {
  await new Promise(resolve => setTimeout(resolve, 200));
  
  // In a real app: GET /api/pipeline-configs/{workflowCode}
  const data = await fetchJSON('pipeline_configs/pipeline_config.json');
  if (data.code === workflowCode) {
    return data;
  }
  throw new Error(`Pipeline config not found for workflow_code: ${workflowCode}`);
};

/**
 * Step 4: Find the manual step config from pipeline config
 */
export const findManualStepConfig = (pipelineConfig, stepCode) => {
  if (!pipelineConfig) {
    throw new Error('Pipeline config is required');
  }

  // Prefer new "nodes" structure when available
  if (Array.isArray(pipelineConfig.nodes)) {
    const nodes = pipelineConfig.nodes;
    const codeToMatch = stepCode;

    let node = null;
    if (codeToMatch) {
      node = nodes.find(
        (n) =>
          n.code === codeToMatch &&
          (n.type === 'manual_task' || !n.type)
      );
    }

    if (!node) {
      node = nodes.find((n) => n.type === 'manual_task');
    }

    if (!node) {
      throw new Error(`Step not found for step_code: ${stepCode}`);
    }

    return {
      ...node,
      ai_intelligence_panel: node.data?.ai_intelligence_panel || node.ai_intelligence_panel,
      ai_recommendation: node.data?.ai_recommendation || node.ai_recommendation,
      in_detail_entities: node.data?.in_detail_entities || node.in_detail_entities,
    };
  }

  // Fallback for legacy configs with "steps" array
  if (Array.isArray(pipelineConfig.steps)) {
    const step = pipelineConfig.steps.find(s => s.code === stepCode);
    if (!step) {
      throw new Error(`Step not found for step_code: ${stepCode}`);
    }
    return step;
  }

  throw new Error('Pipeline config has neither nodes nor steps');
};

/**
 * Step 5: Fetch AI Intelligence Cards based on associated_entities
 */
export const fetchAICardsForManualTask = async (associatedEntities) => {
  await new Promise(resolve => setTimeout(resolve, 200));
  
  // Load all AI card JSON files
  const [claimUnderstanding, policyRules, clinical, utilization, synthesis, risk] = await Promise.all([
    fetchJSON('ai_intellegence_cards/claim_understanding.json'),
    fetchJSON('ai_intellegence_cards/policy_rules_insight.json'),
    fetchJSON('ai_intellegence_cards/clinical_intelligence.json'),
    fetchJSON('ai_intellegence_cards/utilization_billing_insight.json'),
    fetchJSON('ai_intellegence_cards/claim_synthesis.json'),
    fetchJSON('ai_intellegence_cards/risk_confidence.json'),
  ]);
  
  const allAICards = [
    claimUnderstanding,
    policyRules,
    clinical,
    utilization,
    synthesis,
    risk,
  ];
  
  // Filter entities that are AI cards (exclude CLAIM_DETAILS, etc.)
  const aiCardEntities = associatedEntities.filter(e => 
    e.entity_cd.startsWith('AI_CARD_')
  );
  
  // Match by entity_id (which corresponds to the 'id' field in JSON)
  const cards = aiCardEntities.map(entity => {
    const cardData = allAICards.find(card => card.id === entity.entity_id);
    if (!cardData) {
      console.warn(`AI card not found for entity_id: ${entity.entity_id}`);
      return null;
    }
    return transformCardData(cardData);
  }).filter(Boolean);
  
  return cards;
};

/**
 * Fetch AI Recommended Decision Paths
 */
export const fetchAIRecommendedDecisionPaths = async (associatedEntities) => {
  await new Promise(resolve => setTimeout(resolve, 200));
  
  const entity = associatedEntities.find(e => e.entity_cd === 'AI_RECOMMENDED_DECISION_PATHS');
  if (!entity) {
    return null;
  }
  
  // Load AI recommended decision paths
  const data = await fetchJSON('ai_intellegence_cards/ai_recommended_decision_paths.json');
  
  // Match by entity_id
  if (data.id === entity.entity_id) {
    return data;
  }
  
  return null;
};

/**
 * Transform AI card JSON data to component-friendly format
 */
const transformCardData = (cardData) => {
  const baseCard = {
    id: cardData.id,
    entityId: cardData.entityId,
    icon: getIconForCardKey(cardData.cardKey),
    title: cardData.cardTitle,
    subtitle: cardData.cardSubtitle,
    buttonText: cardData.ctaText,
    template: cardData.template,
    moreInformation: cardData.moreInformation,
    confidencePercent: cardData.confidencePercent,
    evaluationTimestampText: cardData.evaluationTimestampText,
    keySignalsText: cardData.keySignalsText,
    rawApiData: cardData // Store complete API response
  };

  // Parse keySignalsText into array
  const keySignals = cardData.keySignalsText
    ? cardData.keySignalsText.split('\n').map(line => line.replace(/^-\s*/, '').trim()).filter(Boolean)
    : [];

  // Template-specific transformations
  switch (cardData.template) {
    case 'claimUnderstanding':
      return {
        ...baseCard,
        metric: cardData.primaryMetricValueText,
        description: cardData.primaryMetricLabel,
        keySignals: keySignals,
      };

    case 'policyRules':
      return {
        ...baseCard,
        metric: cardData.primaryMetricValueText,
        description: cardData.primaryMetricLabel,
        insights: keySignals,
      };

    case 'clinicalIntelligence':
      return {
        ...baseCard,
        riskLevel: cardData.primaryMetricValueText,
        riskIcon: '↓',
        clinicalSignals: keySignals,
      };

    case 'utilizationBilling':
      return {
        ...baseCard,
        riskLevel: cardData.primaryMetricValueText,
        riskIcon: '↓',
        detection: keySignals[0] || 'Unbundled services detected.',
        varianceLabel: 'Variance Isolated',
        varianceAmount: 'IDR23,300',
      };

    case 'claimSynthesis':
      const auditMatch = cardData.keySignalsText.match(/Audit saving:\s*(IDR[\d,]+)\s*\((\d+%)\)/);
      return {
        ...baseCard,
        auditSavingLabel: 'Audit saving',
        auditSavingAmount: auditMatch ? auditMatch[1] : 'IDR23,300',
        auditSavingPercentage: auditMatch ? auditMatch[2] : '(17%)',
        finalPayableLabel: 'Final payable',
        finalPayableAmount: cardData.primaryMetricValueText,
      };

    case 'riskConfidence':
      return {
        ...baseCard,
        confidenceScore: cardData.primaryMetricValueText,
        confidenceLabel: cardData.primaryMetricLabel,
        riskSignals: keySignals,
      };

    default:
      return {
        ...baseCard,
        metric: cardData.primaryMetricValueText,
        description: cardData.primaryMetricLabel,
      };
  }
};

/**
 * Get icon emoji based on card key
 */
const getIconForCardKey = (cardKey) => {
  const iconMap = {
    'CLAIM_UNDERSTANDING': '🗄️',
    'POLICY_RULES_INSIGHT': '🛡️',
    'CLINICAL_INTELLIGENCE': '🩺',
    'UTILIZATION_BILLING_INSIGHT': '📊',
    'CLAIM_SYNTHESIS': '🧠',
    'RISK_CONFIDENCE': '📈',
  };
  return iconMap[cardKey] || '📄';
};

/**
 * MAIN ORCHESTRATION FUNCTION - Using Real API
 * Fetches data from the actual API endpoint
 * @param {string} manualTaskId - Manual task instance ID
 * @param {string} workflowCode - Workflow code (optional, will fetch from task if not provided)
 */
export const fetchWorkbenchDataFromAPI = async (manualTaskId, workflowCode = null, workflowInstanceId = null) => {
  try {
    // Fetch manual task instance from API
    const data = await fetchManualTaskInstanceFromAPI(manualTaskId);
    
    // Extract pipeline code and instance ID from task if not provided
    const effectivePipelineCode =
      // Primary: manual_task_instance.pipeline_cd
      data.pipeline_cd ||
      // Fallbacks for backward compatibility or navigation overrides
      workflowCode ||
      data.pipeline_code ||
      data.workflow_code ||
      data.code;
    const effectivePipelineInstanceId =
      workflowInstanceId ||
      data.pipeline_instance_id ||
      data.pipeline_id ||
      data.workflow_instance_id;
    
    // Fetch pipeline configuration
    let workflowConfig = null;
    let stepConfig = null;
    if (effectivePipelineCode) {
      try {
        workflowConfig = await fetchWorkflowConfigByCode(effectivePipelineCode);
        
        // Find the manual task step configuration from nodes/steps
        try {
          const stepCode = data.step_code || data.code;
          stepConfig = findManualStepConfig(workflowConfig, stepCode);
        } catch (stepError) {
          console.warn('Could not resolve manual task step config:', stepError);
        }
      } catch (configError) {
        console.warn('Could not fetch workflow config:', configError);
      }
    }
    
    // Fetch pipeline execution (pipeline instance)
    let workflowInstance = data.pipeline_instance || data.pipeline_execution || null;
    if (effectivePipelineInstanceId && !workflowInstance) {
      try {
        workflowInstance = await fetchWorkflowInstanceFromAPI(effectivePipelineInstanceId);
      } catch (instanceError) {
        console.warn('Could not fetch pipeline execution:', instanceError);
      }
    }
    
    // Extract session state - prioritize workflow_instance.session_state.workflow_vars
    let sessionState = {};
    if (workflowInstance?.session_state?.workflow_vars) {
      sessionState = workflowInstance.session_state.workflow_vars;
    } else if (data.session_state?.workflow_vars) {
      sessionState = data.session_state.workflow_vars;
    } else if (workflowInstance?.session_state) {
      sessionState = workflowInstance.session_state;
    } else if (data.session_state) {
      sessionState = data.session_state;
    }
    
    // Add caseId from input/output if not in session_state
    if (!sessionState.caseId && data.input?.caseId) {
      sessionState.caseId = data.input.caseId;
    }
    if (!sessionState.caseId && data.output?.caseId) {
      sessionState.caseId = data.output.caseId;
    }
    
    // Extract data_points from workflow config for header rendering
    const dataPoints = workflowConfig?.data_points || data.data_points || data.pipeline_config?.data_points || [];
    
    // Fetch associated entities and transform to AI Intelligence Cards
    let aiCards = [];
    let aiRecommendations = null;
    let aiIntelligencePanelConfig = null;
    let entityConfigs = {};
    
    if (data.associated_entities && Array.isArray(data.associated_entities)) {
      try {
        const entities = await fetchAssociatedEntities(data.associated_entities);
        
        // Fetch entity configurations for all unique entity codes
        const uniqueEntityCodes = [...new Set(entities.map(e => e.entity_cd))];
        const configPromises = uniqueEntityCodes.map(async (entityCd) => {
          try {
            const config = await fetchEntityConfig(entityCd);
            return { entityCd, config };
          } catch (error) {
            console.warn(`Failed to fetch config for ${entityCd}:`, error);
            return { entityCd, config: null };
          }
        });
        
        const configResults = await Promise.all(configPromises);
        configResults.forEach(({ entityCd, config }) => {
          if (config) {
            entityConfigs[entityCd] = config;
          }
        });
        
        // Get AI Intelligence Panel configuration from step config
        if (stepConfig?.ai_intelligence_panel) {
          aiIntelligencePanelConfig = stepConfig.ai_intelligence_panel;
          
          // Filter AI cards based on step config entities if available
          const configuredEntityCodes = stepConfig.ai_intelligence_panel.entities?.map(e => e.entity_cd) || [];
          
          if (configuredEntityCodes.length > 0) {
            // Only include cards that are configured in the step
            aiCards = entities
              .filter(entity => configuredEntityCodes.includes(entity.entity_cd))
              .map(entity => {
                const cardData = entity.data || entity;
                const transformedCard = transformCardData(cardData);
                // Attach entity config if available
                if (entityConfigs[entity.entity_cd]) {
                  transformedCard.entityConfig = entityConfigs[entity.entity_cd];
                }
                return transformedCard;
              });
          } else {
            // Fallback: include all AI_CARD_ entities
            aiCards = entities
              .filter(entity => entity.entity_cd && entity.entity_cd.startsWith('AI_CARD_'))
              .map(entity => {
                const cardData = entity.data || entity;
                const transformedCard = transformCardData(cardData);
                // Attach entity config if available
                if (entityConfigs[entity.entity_cd]) {
                  transformedCard.entityConfig = entityConfigs[entity.entity_cd];
                }
                return transformedCard;
              });
          }
        } else {
          // No step config: include all AI_CARD_ entities
          aiCards = entities
            .filter(entity => entity.entity_cd && entity.entity_cd.startsWith('AI_CARD_'))
            .map(entity => {
              const cardData = entity.data || entity;
              const transformedCard = transformCardData(cardData);
              // Attach entity config if available
              if (entityConfigs[entity.entity_cd]) {
                transformedCard.entityConfig = entityConfigs[entity.entity_cd];
              }
              return transformedCard;
            });
        }
        
        // Fetch AI Recommended Decision Paths from step config or associated entities
        const recommendationEntityCd = stepConfig?.ai_recommendation?.entity_cd || 'AI_RECOMMENDED_DECISION_PATHS';
        const recommendationEntity = entities.find(
          entity => entity.entity_cd === recommendationEntityCd
        );
        if (recommendationEntity) {
          aiRecommendations = recommendationEntity.data || recommendationEntity;
          // Attach entity config for recommendations if available
          if (entityConfigs[recommendationEntityCd]) {
            aiRecommendations.entityConfig = entityConfigs[recommendationEntityCd];
          }
        }
      } catch (error) {
        console.warn('Failed to fetch associated entities:', error);
      }
    }
    
    // Fallback for AI recommendations from manual task data
    if (!aiRecommendations && data.ai_recommendations) {
      aiRecommendations = data.ai_recommendations;
    }
    if (!aiRecommendations && data.output?.recommendations) {
      aiRecommendations = {
        summary: data.output.cardSubtitle || '',
        recommendations: Array.isArray(data.output.recommendations) ? data.output.recommendations : []
      };
    }
    
    // Extract in_detail_entities from API response (contains entity_cd and entity_id)
    const inDetailEntities = data.in_detail_entities || [];
    
    // Return the API response with safe defaults
    return {
      manualTask: data,
      pipelineInstance: workflowInstance,
      workflowInstance: workflowInstance,
      pipelineConfig: data.pipeline_config || null,
      workflowConfig: workflowConfig,
      stepConfig: stepConfig || data.step_config || null,
      aiCards: aiCards.length > 0 ? aiCards : (data.ai_cards || []),
      aiRecommendations: aiRecommendations,
      aiIntelligencePanelConfig: aiIntelligencePanelConfig,
      inDetailEntities: inDetailEntities,
      entityConfigs: entityConfigs,
      sessionState: sessionState,
      dataPoints: dataPoints,
    };
  } catch (error) {
    console.error('Error fetching workbench data from API:', error);
    throw error;
  }
};

/**
 * MAIN ORCHESTRATION FUNCTION - Using Mock Data
 * Follows the complete data flow from README.md
 */
export const fetchWorkbenchData = async (manualTaskId = 1) => {
  try {
    // Step 1: Load manual task instance
    const manualTask = await fetchManualTaskInstance(manualTaskId);
    // Step 2: Load pipeline instance using workflow_instance_id
    const pipelineInstance = await fetchPipelineInstance(manualTask.workflow_instance_id);
    
    // Step 3: Load pipeline config using workflow_code
    const pipelineConfig = await fetchPipelineConfig(pipelineInstance.workflow_code);
    
    // Step 4: Find the manual step config
    const stepConfig = findManualStepConfig(pipelineConfig, manualTask.step_code);
    
    // Step 5: Fetch AI Intelligence Cards based on associated_entities
    const aiCards = await fetchAICardsForManualTask(manualTask.associated_entities);
    
    // Step 6: Fetch AI Recommended Decision Paths
    const aiRecommendations = await fetchAIRecommendedDecisionPaths(manualTask.associated_entities);
    
    // Return complete workbench data
    return {
      manualTask,
      pipelineInstance,
      pipelineConfig,
      stepConfig,
      aiCards,
      aiRecommendations,
      sessionState: pipelineInstance.session_state,
      dataPoints: pipelineConfig.data_points,
    };
  } catch (error) {
    console.error('Error fetching workbench data:', error);
    throw error;
  }
};

export const fetchClaimDetails = async (caseId) => {
  const user = getUserData();
  const companyCd = user?.companyCd || 'mms'
  const API_URL_CLAIM_DETAILS =
    `/api/v1/mms_persistence/claim_details_${companyCd?.toLowerCase()}/fetch?skip=0&limit=10`;

  const API_URL_POLICY_DETAILS =
    `/api/v1/mms_persistence/policy_details_${companyCd?.toLowerCase()}/fetch?skip=0&limit=10`;

  const API_URL_PERSON_PROFILE =
    `/api/v1/mms_persistence/person_profile_${companyCd?.toLowerCase()}/fetch?skip=0&limit=10`;

  const API_URL_PERSON_RESUME =
    `/api/v1/mms_persistence/medical_resume_${companyCd?.toLowerCase()}/fetch?skip=0&limit=10`;

  const API_URL_CLAIM_HISTORY =
    `/api/v1/mms_persistence/claim_history_${companyCd?.toLowerCase()}/fetch?skip=0&limit=10`;
  
  const API_URL_DISCHARGE_SUMMARY =
    `/api/v1/mms_persistence/discharge_summary_${companyCd?.toLowerCase()}/fetch?skip=0&limit=10`;
  const API_URL_INVOICE_DETAILS = 
    `/api/v1/mms_persistence/invoice_${companyCd?.toLowerCase()}/fetch?skip=0&limit=10`;

  const filter = { caseId };

  try {
    const [claimRes, policyRes, profileRes, personResume, claimHistory, dischargeSummary, invoiceDetails] = await Promise.all([
      post(API_URL_CLAIM_DETAILS, { filter }),
      post(API_URL_POLICY_DETAILS, { filter }),
      post(API_URL_PERSON_PROFILE, { filter }),
      post(API_URL_PERSON_RESUME, { filter }),
      post(API_URL_CLAIM_HISTORY, { filter }),
      post(API_URL_DISCHARGE_SUMMARY, { filter }),
      post(API_URL_INVOICE_DETAILS, { filter }),
    ]);

    const combinedData = {
      claimDetails: claimRes[0],
      policyDetails: policyRes[0],
      personProfile: profileRes[0],
      personResume: personResume[0],
      claimHistory: claimHistory[0],
      dischargeSummary: dischargeSummary[0],
      invoiceDetails: invoiceDetails[0]
    };

    return combinedData;
  } catch (error) {
    console.error("Error fetching claim-related data:", error);
    throw error; // better to rethrow for caller handling
  }
};

export const executeWorkflowAsync = async ( workflowCd, userId, query, caseId, claimedAmount, insuredName ) => {
  const API_URL =
    "/api/v1/mms_ai_executors/workflow/execute/async";

  const payload = {
    workflow_cd: workflowCd,
    user_id: userId,
    query,
    payload: {
      caseId,
      claimedAmount,
      insuredName
    },
  };
  
  try {
    const response = await post(API_URL, payload)

    console.log("Workflow execution response --->", response);
    return response;
  } catch (error) {
    console.error("Error executing workflow:", error);
    throw error;
  }
};


export default {
  fetchManualTaskInstance,
  fetchManualTaskInstanceFromAPI,
  fetchWorkflowConfigByCode,
  fetchWorkflowInstanceFromAPI,
  fetchEntityConfig,
  fetchEntityData,
  fetchAssociatedEntities,
  fetchPipelineInstance,
  fetchPipelineConfig,
  findManualStepConfig,
  fetchAICardsForManualTask,
  fetchAIRecommendedDecisionPaths,
  fetchWorkbenchData,
  fetchWorkbenchDataFromAPI,
};
